import {
    AttachmentInfo,
    AttachmentProps,
    AttachmentToolbarProps,
    UploadConfig,
} from '@src/slide/domain/ui-model/element-modeles/property/attachment/interface';
import {
    AttachmentUploadInterface,
    IStatusChangeOptions,
    AttachmentUploadListener,
    AttachmentComponentInterface,
    IExportModalOptions,
    FileType, EStatusEvents,
} from './type';
import { JSXElementConstructor } from 'react';
import ArkLoader from '@src/js/common/ark-extensions';
import componentConfig from '@src/js/common/ark-extensions/dc-components/attachment-uploader/component-config';
import ENV from '@src/slide/infrastructure/module/dependent/runtime-environment/enviroment';
import { uploadImageBlob } from '@src/slide/infrastructure/module/dependent/image/image-toolkit/cgi-upload';
import MediaUtils from '@editor/src/page/index/components/canvas/element-types/image-element/media-container/media-utils';
import { hideMqqNavigation, keepSafeAreaInTencentDocApp, showMqqNavigation } from '@src/slide/infrastructure/module/independent/mobile/mqq-navigation';
import { SlideDataServices } from '@src/slide/domain/slide-app/core/slide-data-services.core';
import { CommandSource } from '@tencent/slide-sdk-model/src/manager/command-manager';
import { AppServiceProvider } from '@src/slide/application/app-service-provider';
import { IEditorAppServices } from '@src/slide/application/entry/toc/editor/base/base-editor-app-service.interface';
import { isVideoElement } from '@src/slide/domain/ui-model/elements/element-judge';
import { network } from '@tencent/netcheck';
import Snackbar from '@tencent/dui/lib/components/Snackbar';
import i18n from '@i18next/i18n';

const MAX_LIMIT_COUNT = 2; // 限制选择文件数
const LOCAL_EDIT_TIMEOUT = 15000; // 本地编辑附件上传超时阈值
export const FILE_TYPE = { // 限制选择文件类型
    video: [
        'wmv',
        'asf',
        'asx',
        'rm',
        'rmvb',
        'mp4',
        '3gp',
        'mov',
        'm4v',
        'avi',
        'dat',
        'mkv',
        'flv',
        'vob',
        'mpeg',
        'mpg',
    ],
    audio: [
        'cd',
        'wave',
        'aiff',
        'mp3',
        'm4a',
        'midi',
        'wma',
        'realaudio',
        'vqf',
        'ogg',
        'amr',
        'ape',
        'flac',
        'aac',
        'oggvorbis',
        'wav',
    ],
    image: [
        'raw',
        'bmp',
        'pcx',
        'tiff',
        'jpeg',
        'tga',
        'exif',
        'fpx',
        'svg',
        'psd',
        'cdr',
        'pcd',
        'dxf',
        'ufo',
        'eps',
        'png',
        'jpg',
        'wmf',
        'pcx',
        'gif',
        'webp',
        'svg+xml',
        'tif',
    ],
    doc: [
        'pptx',
        'ppt',
        'pot',
        'potx',
        'pps',
        'ppsx',
        'dps',
        'dpt',
        'pptm',
        'potm',
        'ppsm',
        'doc',
        'dot',
        'wps',
        'wpt',
        'docx',
        'dotx',
        'docm',
        'dotm',
        'xls',
        'xlt',
        'et',
        'ett',
        'xlsx',
        'xltx',
        'csv',
        'xlsb',
        'xlsm',
        'xltm',
        'ets',
        'pdf',
        'txt',
    ],
};
export const FILE_TYPE_IMAGE = FILE_TYPE.image.map(format => `image/${format}`).join(', '); // 图片格式
export const FILE_TYPE_AUDIO = FILE_TYPE.audio.map(format => `audio/${format}`).join(', '); // 音频格式

/**
 * 处理附件上传相关逻辑以及获取uploader ui
 */
export default class AttachmentUploader implements AttachmentUploadInterface {
    private static instance;

    public static getInstance(): AttachmentUploader {
        if (!AttachmentUploader.instance) {
            AttachmentUploader.instance = new AttachmentUploader();
        }
        return AttachmentUploader.instance;
    }

    private attachmentModule: AttachmentComponentInterface;

    private isUploadComponentLoading = false;

    private uploadListeners: AttachmentUploadListener[] = [];

    public get hasInit(): boolean {
        return !!this.attachmentModule;
    }

    public get dcAttachmentModule(): AttachmentComponentInterface {
        return this.attachmentModule;
    }

    public async initUploader(): Promise<void> {
        const config = this.getConfig();

        if (!this.attachmentModule && !this.isUploadComponentLoading) {
            this.isUploadComponentLoading = true;

            try {
                const result = await this.loadUploadComponent();
                this.isUploadComponentLoading = false;

                if (!result) {
                    throw new Error('Empty Component Result');
                }

                this.attachmentModule = result;
                await result.mount(config);
                // 注册上传状态监听
                this.attachmentModule.onStatusChange(this.uploadStatusChange);
                // 加载完毕后发送通知
                this.uploadListeners.forEach((ls) => {
                    ls.onUpLoaderInit?.();
                });
                // 监听redo undo, 判断是否需要重新上传附件
                // this.addUndoRedoListener();
            } catch (error) {
                this.isUploadComponentLoading = false;
                window.log?.error(`load upload dc component error: ${error.message}`);
            }
        }
    }

    /**
     * 触发上传组件进行上传，并把上传之后返回的文件信息发送出去
     */
    public async upload(args?: { acceptFileType?: FileType, inputConfig?: { file: File } }) {
        if (!ENV.IS_LOCAL_EDIT && !network.isOnline) {
            Snackbar.show({
                message: i18n.t('网络异常，不支持插入附件，请在网络正常后再试。'),
                type: 'info',
            });
            return;
        }

        if (!this.hasInit) {
            await this.initUploader();
        }

        await this.attachmentModule?.upload(args);
    }

    public async reUploadAttachment(attachmentId: string) {
        if (!this.hasInit) {
            await this.initUploader();
        }
        await this.attachmentModule?.reUploadAttachment(attachmentId);
    }

    public showAttachment(id: string, container: HTMLElement, options?: AttachmentInfo): void {
        this.attachmentModule?.showAttachment(id, container, options);
    }

    public removeAttachment(id: string): void {
        this.attachmentModule?.removeAttachment(id);
    }

    public abortUpload(id: string): void {
        this.attachmentModule?.abortUpload(id);
    }

    public pauseAttachment(id: string): void {
        this.attachmentModule?.pauseAttachment(id);
    }

    public resumeAttachment(id: string): void {
        this.attachmentModule?.resumeAttachment(id);
    }

    public getAttachmentComponent(): JSXElementConstructor<AttachmentProps> {
        return this.attachmentModule?.getAttachmentComponent();
    }

    public getToolbarComponent(): JSXElementConstructor<AttachmentToolbarProps> {
        return this.attachmentModule?.getToolbarComponent();
    }

    /**
     * 显示导出模态窗口
     * @param config
     */
    public showExportModal(config: IExportModalOptions) {
        return this.attachmentModule?.showExportModal(config);
    }

    /**
     * 附件下载
     * @param objKey 附件上传完成后获取到的字符串,传入该参数下载的是上传完成后-云端的文件
     * @param attachmentId 附件上传过程中获取到的字符串，传入该参数下载的是上传完成前-本地的文件
     */
    public download(objKey: string, attachmentId?: string): void {
        this.attachmentModule?.download(objKey, attachmentId);
    }

    /**
     * 查看附件
     * @param attachmentInfo
     * @returns
     */
    public viewAttachmentInfo(attachmentInfo: AttachmentInfo) {
        return this.attachmentModule?.viewAttachmentInfo(attachmentInfo, SlideDataServices.DocInfoManager()?.padId ?? '', {
            onFullScreenChange: (isFullScreen) => {
                // 全屏预览时隐藏和显示移动端导航条，否则会遮住全屏预览的关闭按钮
                if (isFullScreen) {
                    hideMqqNavigation();
                    keepSafeAreaInTencentDocApp();
                } else {
                    showMqqNavigation();
                    SlideDataServices.SlideTitleManager()?.updateTitle();
                }
            },
        });
    }

    public addUploadListener(listener: AttachmentUploadListener) {
        if (!this.uploadListeners.includes(listener)) {
            this.uploadListeners.push(listener);
        }
    }

    public removeUploadListener(listener: AttachmentUploadListener) {
        const index = this.uploadListeners.indexOf(listener);
        if (index >= 0) {
            this.uploadListeners.splice(index, 1);
        }
    }

    private uploadStatusChange = (opt: IStatusChangeOptions) => {
        this.uploadListeners.forEach((ls) => {
            ls.onStatusChanges?.(opt);
        });
    };

    private getConfig(): UploadConfig {
        if (ENV.IS_PC_LOCAL_EDIT) {
            return {
                maxUploadNumber: MAX_LIMIT_COUNT,
                // TODO 本地编辑没有padId导致组件无法渲染预览图(cPadId)
                padId: SlideDataServices.DocInfoManager()?.padId ?? '',
                // 本地编辑复用 uploadImageBlob 接口（目前只能插入视频）
                uploader: {
                    upload: async ({ file, taskId, onSuccess, onError, onProcess }) => {
                        // 上传进度
                        const onProgress = (percent: number) => {
                            onProcess?.(taskId, { percent });
                        };
                        uploadImageBlob({
                            blob: file,
                            onProgress,
                        })
                            .then(({ url }) => {
                                // 超时配置
                                const timer = setTimeout(() => {
                                    window.log?.error('local_edit upload timeout');
                                    onError?.(taskId);
                                }, LOCAL_EDIT_TIMEOUT);
                                const handleSuccess = () => {
                                    clearTimeout(timer);
                                    onSuccess?.(taskId, url);
                                };
                                // 音频文件无需获取缩略图
                                if (FILE_TYPE_AUDIO.includes(file.type)) {
                                    onProgress(0.99);
                                    handleSuccess();
                                    return;
                                }
                                // 设置封面和宽高
                                MediaUtils.setVideoImageAndSize(url).then(() => {
                                    handleSuccess();
                                })
                                    .catch((error) => {
                                        // 仅封面设置失败，增加日志输出并回调视频上传成功
                                        window.log?.error(`local_edit setVideoImageAndSize error: ${error.message}`);
                                        handleSuccess();
                                    });
                                onProgress(0.99);
                            })
                            .catch((error) => {
                                window.log?.error(`local_edit upload catch error: ${error.message}`);
                                onError?.(taskId);
                            });
                        return;
                    },
                },
            };
        }

        return {
            maxUploadNumber: MAX_LIMIT_COUNT,
            padId: SlideDataServices.DocInfoManager()?.padId ?? '',
            multiple: true,
        };
    }

    private async loadUploadComponent() {
        return ArkLoader.initPromise
            .then(() => ArkLoader.extensionService.getExtension<AttachmentComponentInterface>(componentConfig.name));
    }

    private async addUndoRedoListener() {
        // FIXME in dc: dc-video 的重新上传流程似乎有 bug，暂时关闭该功能
        SlideDataServices.SDKDataServices.EventManager()?.commandEvents.onUndoRedoChange((args) => {
            if (args.source === CommandSource.Undo || args.source === CommandSource.Redo) {
                // 等待 mobx 更新数据
                Promise.resolve().then(() => {
                    const attachmentManager = this.getAttachmentManage();
                    const selectionService = this.getSelectionService();
                    const models = selectionService?.selectedElements?.selectedArray;
                    models?.forEach((model) => {
                        if (model && isVideoElement(model)) {
                            const attachmentId = attachmentManager?.getLocalAttachment(model.id as string)?.id;
                            if (attachmentId && (
                                attachmentManager?.isUploadingAttachment(attachmentId)
                                || attachmentManager?.getUploadStatus(model.id as string) === EStatusEvents.ERROR
                            )) {
                                this.reUploadAttachment(attachmentId);
                            }
                        }
                    });
                });
            }
        });
    }

    private getAttachmentManage() {
        return (AppServiceProvider.getInstance().getAppServices() as IEditorAppServices)
            .viewableServiceLayer?.attachmentManager;
    }

    private getSelectionService() {
        return (AppServiceProvider.getInstance().getAppServices() as IEditorAppServices)
            .viewableServiceLayer?.selectionService;
    }
}


